import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button } from '@heroui/react';
import type {
    ChartAccount,
    LocaleCode,
    OperationCategory,
    OperationLineDraft,
    OperationLineSide,
    OperationPairRole,
    OperationTemplateDetail,
    UserRole,
} from '@/api/models';
import type { AccountLabel } from '@/pages/accounting/books/journal/components/journalEntryLines';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { AccessNotice } from '../../components';
import { HistoryDrawer } from '../../history';
import type { OperationsAccess } from '../../operationsAccess';
import {
    DropPairDialog,
    EditorHeader,
    EditorToolbar,
    PairEditor,
    PreviewPanel,
    PublishNotice,
    RolePicker,
} from './components';
import type {
    DraftPair,
    EditorError,
    OpenLine,
    PendingDrop,
    SubtitleClause,
} from './operationEditorMeta';
import type { PreviewResult } from './operationPreview';

type OperationEditorPageViewProps = {
    /** `'create'`: no key yet, nothing fetched, name-only until it exists. */
    mode: 'edit' | 'create';
    access: OperationsAccess;
    canRead: boolean;
    canWrite: boolean;
    isLoading: boolean;
    locale: LocaleCode;
    template: OperationTemplateDetail | null;
    categories: OperationCategory[];
    categoryId: string;
    onCategoryChange: (categoryId: string) => void;
    nameKa: string;
    nameEn: string;
    onNameKaChange: (value: string) => void;
    onNameEnChange: (value: string) => void;
    onCreate: () => void;
    isCreating: boolean;
    pairs: DraftPair[];
    roles: UserRole[];
    accounts: ChartAccount[];
    chartAccounts: ChartAccount[];
    accountLabels: {
        ka: Map<string, AccountLabel>;
        en: Map<string, AccountLabel>;
    };
    effectiveFrom: string;
    sampleGel: string;
    vatRateBps: number;
    preview: PreviewResult;
    /** The version a publish would create — the toolbar's button says it. */
    nextVersion: number;
    /** Pre-filtered clauses for the line under the name. */
    subtitle: SubtitleClause[];
    /** How many pairs the version in force has — decides "remove" vs "drop". */
    publishedPairCount: number;
    openLine: OpenLine | null;
    onOpenLine: (line: OpenLine | null) => void;
    pendingDrop: PendingDrop | null;
    onRequestDropPair: (pairGroup: number) => void;
    onCancelDropPair: () => void;
    onConfirmDropPair: () => void;
    onPairRoleChange: (pairGroup: number, role: OperationPairRole) => void;
    isDirty: boolean;
    isSaving: boolean;
    isPublishing: boolean;
    isBlocking: boolean;
    formError: EditorError | null;
    onSampleChange: (value: string) => void;
    onEffectiveFromChange: (value: string) => void;
    onRolesChange: (roles: UserRole[]) => void;
    onLineChange: (
        pairGroup: number,
        side: OperationLineSide,
        patch: Partial<OperationLineDraft>,
    ) => void;
    onLineKindChange: (
        pairGroup: number,
        side: OperationLineSide,
        kind: 'fixed' | 'slot',
    ) => void;
    onAddPair: () => void;
    onSaveDraft: () => void;
    onPublish: () => void;
    onSetBlocked: (blocked: boolean) => void;
    onOpenHistory: () => void;
    /** The key the history drawer is showing, `null` while it is closed. */
    historyKey: string | null;
    onCloseHistory: () => void;
};

/**
 * The template editor — render only.
 *
 * **The recipe leads and the preview follows, 57 / 43.** They are not two equal
 * panels: the left column is what the author is manipulating and the right is
 * what they check, so the pairs get the width and the journal — three narrow
 * columns of figures — gets what it needs and no more. Below `lg` they stack
 * with the preview second, for the same reason.
 *
 * The publish controls sit in the **header**, not down the right column. `Publish
 * v4` and `in force from 01/09/2026` are halves of one sentence, and the notice
 * that says what publishing will do belongs directly under them rather than
 * beside the preview it has nothing to do with.
 *
 * Three states this View is responsible for telling apart, all of which look
 * superficially like "you cannot edit this":
 *
 * - **No read access at all** — a PIN session or a Seller. The notice replaces
 *   the whole screen and nothing was fetched.
 * - **Read-only** — a BranchAdmin. The recipe and the preview render in full,
 *   every control disabled, and there is no Save or Publish. They can see how an
 *   operation posts; they cannot change it.
 * - **Blocked** — anyone. The recipe is intact and editable, and an Unblock
 *   action sits above it. A blocked operation is retired, not broken.
 */
export const OperationEditorPageView: React.FC<
    OperationEditorPageViewProps
> = ({
    mode,
    access,
    canRead,
    canWrite,
    isLoading,
    locale,
    template,
    categories,
    categoryId,
    onCategoryChange,
    nameKa,
    nameEn,
    onNameKaChange,
    onNameEnChange,
    onCreate,
    isCreating,
    pairs,
    roles,
    accounts,
    chartAccounts,
    accountLabels,
    effectiveFrom,
    sampleGel,
    vatRateBps,
    preview,
    nextVersion,
    subtitle,
    publishedPairCount,
    openLine,
    onOpenLine,
    pendingDrop,
    onRequestDropPair,
    onCancelDropPair,
    onConfirmDropPair,
    onPairRoleChange,
    isDirty,
    isSaving,
    isPublishing,
    isBlocking,
    formError,
    onSampleChange,
    onEffectiveFromChange,
    onRolesChange,
    onLineChange,
    onLineKindChange,
    onAddPair,
    onSaveDraft,
    onPublish,
    onSetBlocked,
    onOpenHistory,
    historyKey,
    onCloseHistory,
}) => {
    const { t } = useTranslation('pages');

    if (!canRead) {
        return (
            <section
                aria-label={t('operations.editor.title')}
                className="flex flex-col gap-4"
                {...getNodeTestIdProp('operation-editor-section')}
            >
                <AccessNotice
                    access={
                        access as Exclude<OperationsAccess, { kind: 'full' }>
                    }
                />
            </section>
        );
    }

    const isBlocked = template?.status === 'BLOCKED';

    return (
        <section
            aria-label={t('operations.editor.title')}
            className="flex flex-col gap-4"
            {...getNodeTestIdProp('operation-editor-section')}
        >
            <EditorHeader
                categories={categories}
                categoryId={categoryId}
                onCategoryChange={onCategoryChange}
                actions={
                    mode === 'create' ? (
                        <Button
                            isDisabled={
                                isCreating || !nameKa.trim() || !nameEn.trim()
                            }
                            onPress={onCreate}
                            size="sm"
                            variant="primary"
                            {...getNodeTestIdProp('operation-editor-create')}
                        >
                            {t('operations.editor.create')}
                        </Button>
                    ) : (
                        <EditorToolbar
                            canWrite={canWrite}
                            effectiveFrom={effectiveFrom}
                            effectiveFromError={
                                formError &&
                                formError.target === 'effectiveFrom'
                                    ? formError.message
                                    : null
                            }
                            isDirty={isDirty}
                            isPublishing={isPublishing}
                            isSaving={isSaving}
                            nextVersion={nextVersion}
                            onEffectiveFromChange={onEffectiveFromChange}
                            onOpenHistory={onOpenHistory}
                            onPublish={onPublish}
                            onSaveDraft={onSaveDraft}
                        />
                    )
                }
                isReadOnly={mode === 'edit' && !canWrite}
                locale={locale}
                nameEn={nameEn}
                nameKa={nameKa}
                onNameEnChange={onNameEnChange}
                onNameKaChange={onNameKaChange}
                subtitle={mode === 'create' ? [] : subtitle}
            />

            {mode === 'create' ? (
                <p
                    className="text-sm text-muted"
                    {...getNodeTestIdProp('operation-editor-create-note')}
                >
                    {t('operations.editor.createNote')}
                </p>
            ) : null}

            {formError && formError.target === 'form' ? (
                <p
                    className="rounded-xl bg-danger-soft px-4 py-3 text-sm text-danger-soft-foreground"
                    {...getNodeTestIdProp('operation-editor-form-error')}
                >
                    {formError.message}
                </p>
            ) : null}

            {mode === 'create' ? null : canWrite ? (
                <PublishNotice
                    activeVersion={template?.activeVersion?.version ?? null}
                    nextVersion={nextVersion}
                />
            ) : (
                <p
                    className="text-xs text-muted"
                    {...getNodeTestIdProp('operation-editor-read-only-note')}
                >
                    {t('operations.editor.readOnlyNote')}
                </p>
            )}

            {mode === 'create' ? null : isBlocked ? (
                <div
                    className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-separator bg-surface-secondary p-4"
                    {...getNodeTestIdProp('operation-editor-blocked-banner')}
                >
                    <p className="text-sm text-muted">
                        {t('operations.editor.blockedNote')}
                    </p>
                    {canWrite ? (
                        <Button
                            isDisabled={isBlocking}
                            onPress={() => onSetBlocked(false)}
                            variant="secondary"
                            {...getNodeTestIdProp('operation-editor-unblock')}
                        >
                            {t('operations.editor.unblock')}
                        </Button>
                    ) : null}
                </div>
            ) : null}

            {mode === 'create' ? null : (
                <div className="grid grid-cols-1 gap-5 lg:grid-cols-[57fr_43fr]">
                    <div className="flex min-w-0 flex-col gap-4">
                        {isLoading ? (
                            <EditorSkeleton />
                        ) : (
                            <>
                                <PairEditor
                                    accountLabels={accountLabels}
                                    accounts={accounts}
                                    chartAccounts={chartAccounts}
                                    isReadOnly={!canWrite}
                                    nextVersion={nextVersion}
                                    onAddPair={onAddPair}
                                    onLineChange={onLineChange}
                                    onLineKindChange={onLineKindChange}
                                    onOpenLine={onOpenLine}
                                    onPairRoleChange={onPairRoleChange}
                                    onRequestDropPair={onRequestDropPair}
                                    openLine={openLine}
                                    pairs={pairs}
                                    publishedPairCount={publishedPairCount}
                                    vatRateBps={vatRateBps}
                                />
                                {formError && formError.target === 'pairs' ? (
                                    <p
                                        className="text-sm text-danger"
                                        {...getNodeTestIdProp(
                                            'operation-editor-pairs-error',
                                        )}
                                    >
                                        {formError.message}
                                    </p>
                                ) : null}
                            </>
                        )}
                    </div>

                    <div className="flex min-w-0 flex-col gap-4">
                        <PreviewPanel
                            accounts={accounts}
                            isReadOnly={isLoading}
                            onSampleChange={onSampleChange}
                            preview={preview}
                            sampleGel={sampleGel}
                            vatRateBps={vatRateBps}
                        />
                        <RolePicker
                            errorText={
                                formError && formError.target === 'roles'
                                    ? formError.message
                                    : null
                            }
                            isReadOnly={!canWrite || isLoading}
                            onChange={onRolesChange}
                            roles={roles}
                        />
                        {canWrite && !isBlocked ? (
                            <Button
                                className="self-start"
                                isDisabled={isBlocking || isLoading}
                                onPress={() => onSetBlocked(true)}
                                variant="ghost"
                                {...getNodeTestIdProp('operation-editor-block')}
                            >
                                <Icon aria-hidden icon="gravity-ui:ban" />
                                {t('operations.editor.block')}
                            </Button>
                        ) : null}
                    </div>
                </div>
            )}

            {mode === 'create' ? null : (
                <DropPairDialog
                    effectiveFrom={effectiveFrom}
                    nextVersion={nextVersion}
                    onCancel={onCancelDropPair}
                    onConfirm={onConfirmDropPair}
                    pending={pendingDrop}
                />
            )}

            {/* The same drawer the catalog opens, over the editor this time.
                No `onOpenEditor`: the editor is where that button would lead,
                so it is absent here rather than present and inert. */}
            <HistoryDrawer onClose={onCloseHistory} operationKey={historyKey} />
        </section>
    );
};

/**
 * Stands in for the pairs while the template and the chart load.
 *
 * Only the recipe skeletons: the preview keeps its real headers and its totals
 * row, the role chips keep their labels, and the footer notes stay — the
 * loading-state standard. Two placeholder cards rather than one, because a
 * template with a single pair is the exception here, not the rule.
 */
const EditorSkeleton: React.FC = () => (
    <div
        className="flex flex-col gap-4"
        {...getNodeTestIdProp('operation-editor-skeleton')}
    >
        {[0, 1].map((index) => (
            <div
                className="flex flex-col gap-3 rounded-2xl border border-separator bg-surface p-4"
                key={index}
            >
                <div className="h-4 w-24 animate-pulse rounded bg-surface-secondary" />
                <div className="h-16 w-full animate-pulse rounded-xl bg-surface-secondary" />
                <div className="h-16 w-full animate-pulse rounded-xl bg-surface-secondary" />
            </div>
        ))}
    </div>
);
